import React, { useRef } from 'react';
import { useOverlay } from 'react-aria';
import { Button, Dialog, Popover } from 'react-aria-components';
import cn from '@/utils/cn';
import styles from './PlanningDrawer.module.scss';
import { PlanningHeader } from './PlanningHeader';
import { TabbedContent } from './TabbedContent';
import { BUTTON_CLASSES, PlanningContext, Resource, TRANSLATIONS, usePlanningDrawer } from './utils';

interface PlanningDrawerProps {
  planningContext: PlanningContext;
  printPdfPath: string;
  resources: Resource[];
}

export const PlanningButton = ({ printPdfPath, resources, planningContext }: PlanningDrawerProps) => {
  const {
    open,
    isOpen,
    toggleOpen,
    isExpanded,
  } = usePlanningDrawer();

  if (resources.length === 0) return null;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef(null);

  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose: toggleOpen,
    },
    overlayRef,
  );
  return (
    <>
      <Button
        ref={buttonRef}
        aria-expanded={isOpen}
        aria-label={TRANSLATIONS.dialog.open(planningContext)}
        className={cn(BUTTON_CLASSES,
          { 'tw-invisible': isOpen },
          { 'tw-absolute -tw-top-8 tw-left-0': isExpanded })}
        onPress={open}
        {...underlayProps}
      >
        {TRANSLATIONS.title(planningContext)}
        <span className={`fa ${isOpen ? 'fa-close' : 'fa-chevron-down'}`} />
      </Button>
      <Popover
        className={cn(styles.popover,
          { 'tw-px-[4rem]': isExpanded })}
        isOpen={isOpen}
        placement={isExpanded ? 'bottom start' : 'bottom end'}
        shouldFlip={false}
        style={{ zIndex: 'unset' }}
        triggerRef={buttonRef}
        onOpenChange={toggleOpen}
      >
        <div
          {...overlayProps}
          ref={overlayRef}
          className="tw-fixed tw-z-[1100] tw-inset-0 tw-bg-black tw-bg-opacity-75"
        />

        <Dialog
          aria-label={TRANSLATIONS.dialog.standard(planningContext)}
          className={cn('tw-relative tw-flex tw-flex-col tw-bg-white tw-rounded-lg tw-border-t-[#F16E00]',
            'tw-border-t-8 tw-border-solid tw-z-[1200] tw-max-w-[50vw] tw-h-[85vh]',
            { 'tw-max-w-none tw-w-full tw-h-[90vh]': isExpanded })}
        >
          <Button
            aria-expanded={isOpen}
            aria-label={TRANSLATIONS.dialog.open(planningContext)}
            className={cn(BUTTON_CLASSES, 'tw-absolute -tw-top-12 tw-right-0 tw-shadow-none',
              { 'tw-hidden': isExpanded })}
            onPress={toggleOpen}
          >
            {TRANSLATIONS.title(planningContext)}
            <span className={`fa ${isOpen ? 'fa-close' : 'fa-chevron-down'}`} />
          </Button>
          <PlanningHeader planningContext={planningContext} printPdfPath={printPdfPath} />
          <TabbedContent resources={resources} />
        </Dialog>
      </Popover>
    </>
  );
};
